import React, { useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/Button';
import { GoalCard } from '../components/GoalCard';
import { WebLayout } from '../components/WebLayout';
import { CreateGoalModal } from '../components/CreateGoalModal';
import { EditGoalModal } from '../components/EditGoalModal';
import { useUser } from '../contexts/UserContext';
type FilterType = 'All' | 'Work' | 'Study' | 'Personal' | 'Health' | 'Others';
export function GoalsPage() {
  const {
    goals,
    toggleGoal,
    deleteGoal
  } = useUser();
  const [filter, setFilter] = useState<FilterType>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState<any>(null);
  const filters: FilterType[] = ['All', 'Work', 'Study', 'Personal', 'Health', 'Others'];
  const filteredGoals = goals.filter(goal => {
    const matchesFilter = filter === 'All' || goal.category === filter;
    const matchesSearch = goal.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });
  const handleEditGoal = (goal: any) => {
    setEditingGoal(goal);
    setShowEditModal(true);
  };
  return <WebLayout data-id="element-813">
      {/* Desktop Header */}
      <header className="hidden lg:block px-8 pt-8 pb-6 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#252936]" data-id="element-814">
        <div className="max-w-7xl mx-auto" data-id="element-815">
          <div className="flex items-center justify-between mb-6" data-id="element-816">
            <div data-id="element-817">
              <h1 className="text-3xl font-fredoka font-bold text-game-dark dark:text-white mb-2" data-id="element-818">
                Your Quests
              </h1>
              <p className="text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-819">
                Manage and track all your goals in one place
              </p>
            </div>
            <Button size="lg" icon={<Plus className="w-5 h-5" data-id="element-821" />} onClick={() => setShowCreateModal(true)} data-id="element-820">
              New Quest
            </Button>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-822">
            <div className="flex-1 relative" data-id="element-823">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-824" />
              <input type="text" placeholder="Search quests..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-2xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-[#252936] text-game-dark dark:text-white font-quicksand focus:outline-none focus:border-game-purple transition-colors" data-id="element-825" />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0" data-id="element-826">
              {filters.map(f => <button key={f} onClick={() => setFilter(f)} className={`px-4 py-3 rounded-xl font-fredoka font-bold text-sm whitespace-nowrap transition-all ${filter === f ? 'bg-game-purple text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-827">
                  {f}
                </button>)}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="lg:hidden px-6 pt-6 pb-4" data-id="element-828">
        <div className="flex justify-between items-center mb-6" data-id="element-829">
          <h1 className="text-3xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-830">
            Quests
          </h1>
          <Link to="/create-goal" data-id="element-831">
            <Button size="sm" icon={<Plus className="w-5 h-5" data-id="element-833" />} data-id="element-832">
              New
            </Button>
          </Link>
        </div>

        {/* Search */}
        <div className="relative mb-4" data-id="element-834">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-835" />
          <input type="text" placeholder="Search quests..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-2xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-[#252936] text-game-dark dark:text-white font-quicksand focus:outline-none focus:border-game-purple" data-id="element-836" />
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2" data-id="element-837">
          {filters.map(f => <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-xl font-fredoka font-bold text-sm whitespace-nowrap transition-all ${filter === f ? 'bg-game-purple text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`} data-id="element-838">
              {f}
            </button>)}
        </div>
      </header>

      {/* Goals Grid */}
      <main className="px-6 lg:px-8 py-6 lg:py-8" data-id="element-839">
        <div className="max-w-7xl mx-auto" data-id="element-840">
          {filteredGoals.length === 0 ? <div className="text-center py-16" data-id="element-841">
              <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-842">
                <Search className="w-12 h-12 text-gray-400" data-id="element-843" />
              </div>
              <h3 className="font-fredoka font-bold text-xl text-game-dark dark:text-white mb-2" data-id="element-844">
                No quests found
              </h3>
              <p className="text-gray-500 dark:text-gray-400 font-quicksand mb-6" data-id="element-845">
                {searchQuery ? 'Try adjusting your search' : 'Create your first quest to get started!'}
              </p>
              <Button icon={<Plus className="w-5 h-5" data-id="element-847" />} onClick={() => setShowCreateModal(true)} className="hidden lg:inline-flex" data-id="element-846">
                Create Quest
              </Button>
              <Link to="/create-goal" className="lg:hidden" data-id="element-848">
                <Button icon={<Plus className="w-5 h-5" data-id="element-850" />} data-id="element-849">
                  Create Quest
                </Button>
              </Link>
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-851">
              {filteredGoals.map((goal, index) => <motion.div key={goal.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.05
          }} data-id="element-852">
                  <GoalCard goal={goal} onToggle={() => toggleGoal(goal.id)} onEdit={() => handleEditGoal(goal)} onDelete={() => deleteGoal(goal.id)} data-id="element-853" />
                </motion.div>)}
            </div>}
        </div>
      </main>

      {/* Floating Action Button - Mobile Only */}
      <div className="lg:hidden fixed bottom-24 right-6 z-30" data-id="element-854">
        <Link to="/create-goal" data-id="element-855">
          <motion.button whileHover={{
          scale: 1.1
        }} whileTap={{
          scale: 0.9
        }} className="bg-game-dark text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center border-4 border-white dark:border-[#1a1d29]" data-id="element-856">
            <Plus className="w-6 h-6" data-id="element-857" />
          </motion.button>
        </Link>
      </div>

      {/* Create Goal Modal - Desktop Only */}
      <CreateGoalModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} data-id="element-858" />

      {/* Edit Goal Modal */}
      <EditGoalModal isOpen={showEditModal} onClose={() => {
      setShowEditModal(false);
      setEditingGoal(null);
    }} goal={editingGoal} data-id="element-859" />
    </WebLayout>;
}